/**
 * Composer Mode Icon Component
 *
 * Animated icon with a pencil and orbiting sparkles for composer mode indication
 */

import React, { CSSProperties } from "react";

interface ComposerModeIconProps {
  /** Icon size in pixels */
  size?: number;
  /** Additional CSS styles */
  style?: CSSProperties;
  /** Use white colors for dark backgrounds */
  whiteMode?: boolean;
}

/**
 * Sparkle component for orbiting animation
 */
const Sparkle: React.FC<{
  style: CSSProperties;
  size: number;
  color: string;
}> = ({ style, size, color }) => (
  <svg style={style} width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 0l2.4 9.6L24 12l-9.6 2.4L12 24l-2.4-9.6L0 12l9.6-2.4L12 0z" />
  </svg>
);

/**
 * Pencil icon component
 */
const PencilIcon: React.FC<{ size: number; color: string }> = ({ size, color }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
  </svg>
);

/**
 * Animated composer mode icon with pencil and orbiting sparkles
 */
export const ComposerModeIcon: React.FC<ComposerModeIconProps> = ({
  size = 16,
  style = {},
  whiteMode = false,
}) => {
  // Colors based on mode
  const primaryColor = whiteMode ? "#ffffff" : "#106ebe";
  const secondaryColor = whiteMode ? "#f0f0f0" : "#0066cc";

  // Container style
  const containerStyle: CSSProperties = {
    position: "relative",
    display: "inline-block",
    width: size + 8, // Add padding for orbiting sparkles
    height: size + 8,
    ...style,
  };

  // Sparkle positions and animations
  const sparkle1Style: CSSProperties = {
    position: "absolute",
    top: -2,
    left: -2,
    animation: "composer-orbit 3s linear infinite",
    transformOrigin: `${(size + 8) / 2}px ${(size + 8) / 2}px`,
    opacity: 0.8,
  };

  const sparkle2Style: CSSProperties = {
    position: "absolute",
    bottom: -2,
    right: -2,
    animation: "composer-orbit-reverse 3s linear infinite",
    transformOrigin: `${(size + 8) / 2}px ${(size + 8) / 2}px`,
    opacity: 0.6,
  };

  const pencilStyle: CSSProperties = {
    position: "absolute",
    top: 4,
    left: 4,
  };

  return (
    <div style={containerStyle}>
      <div style={pencilStyle}>
        <PencilIcon size={size} color={primaryColor} />
      </div>
      <Sparkle style={sparkle1Style} size={6} color={secondaryColor} />
      <Sparkle style={sparkle2Style} size={5} color={primaryColor} />
    </div>
  );
};
